import { createContext } from 'react';

import type { Nullable } from '@repo/common';
import type { GetCurrentUserQuery, PictureInput, UpdateProfileInput } from '@repo/frontend-shared';

export type CompositeCurrentUserType = Nullable<GetCurrentUserQuery['currentUser']>;
export type CurrentUserReloadResult =
  | { succeeded: true; currentUser: NonNullable<CompositeCurrentUserType> }
  | { succeeded: false; error: unknown };

export type CurrentUserContextValue = {
  currentUser: CompositeCurrentUserType;
  loading: boolean;
  saving: boolean;
  savingPicture: boolean;
  isLoggedIn: boolean;
  isMentor: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isStudent: boolean;
  isPartnerManager: boolean;
  shouldVerifyEmail: boolean;
  updateUserData: (data: UpdateProfileInput) => void | Promise<void>;
  updatePicture: (picture: PictureInput) => void | Promise<void>;
  deletePicture: () => void | Promise<void>;
  reloadCurrentUser: () => Promise<CurrentUserReloadResult>;
  /** Resolves true once the session is ended and the page is leaving; false when the server refused. */
  logout: () => Promise<boolean>;
  /** Like `logout`, but the page lands on `returnTo` (a same-origin path) instead of the default sign-out page. */
  logoutAndReturnTo: (returnTo: string) => Promise<boolean>;
  completeLogout: () => void | Promise<void>;
};

export const CurrentUserContext = createContext<CurrentUserContextValue>({
  loading: true,
  currentUser: null,
  saving: false,
  savingPicture: false,
  isLoggedIn: false,
  isMentor: false,
  isAdmin: false,
  isSuperAdmin: false,
  isStudent: false,
  isPartnerManager: false,
  shouldVerifyEmail: false,
  updateUserData: () => {},
  updatePicture: () => {},
  deletePicture: () => {},
  reloadCurrentUser: async () => ({ succeeded: false, error: new Error('Current user context is unavailable') }),
  logout: async () => false,
  logoutAndReturnTo: async () => false,
  completeLogout: () => {},
});
